import React from "react";

import _ from "lodash";

type ParamsType<T> = {
    list: Array<T>;
    onUpdateList: (newList: Array<T>) => void;
};

export type DragAndDropListHook<E> = {
    onDragStart: (index: number) => void;
    onDragEnd: (index: number) => void;
    onDragOver: (event: React.DragEvent<E>) => void;
    onDragEnter: (index: number) => void;
    onDragLeave: (index: number) => void;
    onDrop: (index: number) => void;
    classes: { [i: number]: Array<string> };
};

function useDragAndDropList<T, E>({ list, onUpdateList }: ParamsType<T>): DragAndDropListHook<E> {
    const [classes, setClasses] = React.useState<{ [i: number]: Array<string> }>({});

    React.useEffect(() => {
        setClasses(() => {
            const newClasses: { [i: number]: Array<string> } = {};

            for (let i = 0; i < list.length; i++) {
                newClasses[i] = [];
            }

            return newClasses;
        });
    }, [list]);

    const [dragged, setDragged] = React.useState<number | undefined>(undefined);

    const addClass = React.useCallback((index: number, className: string) => {
        setClasses((prevClasses) => {
            const newClasses = _.cloneDeep(prevClasses);

            if (newClasses[index]) {
                if (newClasses[index].indexOf(className) === -1) {
                    newClasses[index].push(className);
                }
            }

            return newClasses;
        });
    }, []);

    const removeClass = React.useCallback((index: number, className: string) => {
        setClasses((prevClasses) => {
            const newClasses = _.cloneDeep(prevClasses);

            if (newClasses[index]) {
                const position = newClasses[index].indexOf(className);

                if (position !== -1) {
                    newClasses[index].splice(position, 1);
                }
            }

            return newClasses;
        });
    }, []);

    const onDragStart = React.useCallback(
        (index: number) => {
            addClass(index, "dragging");

            setDragged(index);
        },
        [addClass]
    );

    const onDragEnd = React.useCallback(
        (index: number) => {
            removeClass(index, "dragging");
        },
        [removeClass]
    );

    const onDragOver = React.useCallback((event: React.DragEvent<E>) => {
        event.preventDefault();
    }, []);

    const onDragEnter = React.useCallback(
        (index: number) => {
            addClass(index, "dragover");
        },
        [addClass]
    );

    const onDragLeave = React.useCallback(
        (index: number) => {
            removeClass(index, "dragover");
        },
        [removeClass]
    );

    const onDrop = React.useCallback(
        (index: number) => {
            if (dragged !== undefined) {
                const newList = _.cloneDeep(list);

                if (dragged > index) {
                    newList.splice(index, 0, list[dragged]);

                    newList.splice(dragged + 1, 1);
                } else if (dragged < index) {
                    newList.splice(index + 1, 0, list[dragged]);

                    newList.splice(dragged, 1);
                }

                onUpdateList(newList);
            }
        },
        [onUpdateList, dragged, list]
    );

    return React.useMemo(
        (): DragAndDropListHook<E> => ({
            onDragStart,
            onDragEnd,
            onDragOver,
            onDragEnter,
            onDragLeave,
            onDrop,
            classes,
        }),
        [onDragStart, onDragEnd, onDragOver, onDragEnter, onDragLeave, onDrop, classes]
    );
}

export default useDragAndDropList;
